/**
 * Shared fetch plumbing for every per-resource API module in this directory (auth-api.ts,
 * riders-api.ts, presets-api.ts). Nothing resource-specific lives here — this is the seam that
 * used to be duplicated inline across the old monolithic `api.ts` (docs/V2_ARCHITECTURE.md,
 * CLAUDE.md working rule #8 "typed API client split per resource").
 */
import { API_BASE_URL } from "../config";

export { API_BASE_URL };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function parseOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `API ${res.status} ${res.statusText}: ${body}`);
  }
  return (await res.json()) as T;
}

/**
 * `true` when `err` is the API refusing a WRITE because the caller's email isn't verified yet
 * (`apps/api/src/auth/verified-email.guard.ts#EmailNotVerifiedException` — 403, never 401, so this
 * never overlaps with `authFetch`'s session-expiry handling above). Every caller that catches a
 * write error (riders-list-page, editor-page, save-preset-dialog, preset-menu, upload-object-dialog)
 * checks this FIRST and shows a friendly "verify your email" message instead of the raw API error.
 *
 * Parses the SAME embedded JSON body `profile-page.ts`'s local `humanError()` already extracts —
 * duplicated here (not imported from there) because that helper lives in a page module, and this
 * predicate belongs in the shared api/ layer every one of those call sites already imports from.
 */
export function isEmailNotVerifiedError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const jsonStart = err.message.indexOf("{");
  if (jsonStart === -1) return false;
  try {
    const parsed = JSON.parse(err.message.slice(jsonStart)) as { error?: unknown };
    return parsed.error === "email_not_verified";
  } catch {
    return false;
  }
}

/**
 * The one place a caught write error becomes the text a status line shows (SCRUM-261). Every write
 * path that catches an error is supposed to check `isEmailNotVerifiedError` first — `reportSaveFailure`
 * and `togglePublish` (`editor-page.ts`) both did — but the guest-draft auto-save that runs right
 * after a fresh registration did not, so the very first write a newly-registered guest could trigger
 * showed them the server's raw body (`API 403 Forbidden: {"statusCode":403,"error":"email_not_verified",...}`)
 * instead of a sentence they could act on. Centralised here so the three call sites share one
 * decision instead of three copies of the same ternary that can drift out of sync again.
 */
export function describeWriteError(err: unknown, emailNotVerifiedMessage: string): string {
  return isEmailNotVerifiedError(err) ? emailNotVerifiedMessage : err instanceof ApiError ? err.message : String(err);
}

// --- session storage (localStorage) — the ONLY place any api/* module touches localStorage.
// `auth-service.ts` is the only caller outside this directory; every OTHER api/* module only
// ever READS a token via `authHeader()` below, never the raw storage keys. -------------------

const TOKEN_STORAGE_KEY = "scb_auth_token";
const USER_STORAGE_KEY = "scb_auth_user";
/** The long-lived half of the session (SCRUM-729). localStorage, NOT a cookie: the privacy policy
 *  states sessions are kept in localStorage, and a cookie would bring a consent banner with it. */
const REFRESH_STORAGE_KEY = "scb_refresh_token";

export interface StoredAuthUser {
  id: string;
  email: string;
  /** Admin flag, for showing admin-only UI only. The server's AdminGuard is the real boundary; a
   *  stale stored value (role changed since login) can at most show a button whose endpoint 403s. */
  isAdmin?: boolean;
  /** The account's stored locale (`users.locale`), or `null`/absent if never set — the login/
   *  register response carries it so `i18n/index.ts#resolveInitialLocale` can prefer it over
   *  `localStorage` at boot WITHOUT a second round trip (docs/V2_I18N_PLAN.md phase 2). Stale after
   *  a locale change on a DIFFERENT device until the next login here; on THIS device
   *  `i18n/index.ts#setLocale` keeps it current through `updateStoredUserLocale()` below — it must,
   *  because boot reads this field BEFORE `scb-locale` and a stale value would silently override
   *  every switch the user makes. */
  locale?: string | null;
  /** The password used to sign in was issued by support and must be replaced (SCRUM-131). The app
   *  shell routes on this so the user lands on the form instead of hitting a 403 on whatever they
   *  clicked; the server enforces it independently (`PasswordChangeRequiredGuard`), so clearing
   *  this by hand in devtools buys nothing but a wall of 403s. */
  mustChangePassword?: boolean;
  /** False for an account made by an email link until a password is set (SCRUM-591). Absent on a
   *  session stored before this field existed — read as "has one", so nobody is nagged by mistake. */
  hasPassword?: boolean;
  /** The profile role (Profile → «I am a»), as of sign-in; null/absent when none was chosen. `rental` lands on the
   *  rental desk after sign-in (`app-shell.ts`, SCRUM-647). Routing only — never a right. */
  userRole?: string | null;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function getStoredUser(): StoredAuthUser | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredAuthUser;
  } catch {
    return null; // corrupt localStorage value — treat as logged out rather than throwing
  }
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

/** `refreshToken` is optional so a response without one (an older server) still stores the session. */
export function storeAuth(accessToken: string, user: StoredAuthUser, refreshToken?: string | null): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  if (refreshToken) localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
}

/** Replaces ONLY the refresh token - used after a password change, which revokes every old one and
 *  hands this device a fresh one. */
export function storeRefreshToken(refreshToken: string): void {
  localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
}

/**
 * Patches ONLY the `locale` field of the stored session, leaving the token and every other field
 * untouched. Called by `i18n/index.ts#setLocale` so the language switcher's choice survives the
 * reload it triggers.
 *
 * Without this the switcher is dead for a logged-in user: `resolveInitialLocale()` reads this
 * object BEFORE `scb-locale`, so after the reload the locale captured at LOGIN wins and the page
 * comes back in the old language (SCRUM-127). Writing it here, synchronously, rather than from the
 * `/users/me/locale` response, keeps the fix independent of the network — the server write is
 * best-effort and the reload does not wait for it to succeed.
 *
 * No-op when logged out: an anonymous visitor has no stored user, and `scb-locale` alone already
 * decides their language.
 */
/** Drops the temporary-password flag after the user has set their own password, so the shell stops
 *  forcing the form. Same shape as `updateStoredUserLocale` below and for the same reason: the
 *  stored snapshot is written at login and would otherwise stay stale until the next one. */
export function clearStoredPasswordChangeFlag(): void {
  const user = getStoredUser();
  if (!user) return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...user, mustChangePassword: false }));
}

/** The person set their first password on the page (SCRUM-591) — stop offering it. */
export function markStoredPasswordSet(): void {
  const user = getStoredUser();
  if (!user) return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...user, hasPassword: true }));
}

export function updateStoredUserLocale(locale: string): void {
  const user = getStoredUser();
  if (!user) return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...user, locale }));
}

/** The role was changed (My profile): the stored person carries it too, because `app-shell.ts` picks the
 *  landing page from it — without this the next landing would use the role as of sign-in until a re-login. */
export function updateStoredUserRole(userRole: string): void {
  const user = getStoredUser();
  if (!user) return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...user, userRole }));
}

export function clearStoredAuth(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(REFRESH_STORAGE_KEY);
}

/** `Authorization` header for an authenticated request, or `{}` if logged out — spread into a
 *  fetch's `headers`. */
export function authHeader(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- expired/invalid-token handling ---------------------------------------------------------
// `authFetch` below is the ONE place that decides "this request carried a bearer token, and the
// server rejected it" — i.e. the token is invalid/expired, never "wrong email or password" (that
// only happens on the header-less `/auth/login` call in auth-api.ts, which never goes through
// `authFetch`). `auth-service.ts` (which owns `forceLogout()`) registers itself as the handler at
// import time via `setUnauthorizedHandler`, rather than this file importing auth-service.ts
// directly — http.ts is a transitive dependency of auth-service.ts (auth-service.ts ->
// auth-api.ts -> http.ts), so importing back would create a cycle.

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | undefined;

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

/** How an attempt to renew the session ended (SCRUM-729):
 *   - `renewed`:     the stored access token is new - repeat the request;
 *   - `rejected`:    the server refused the refresh token (or there is none) - the session is over;
 *   - `unavailable`: the exchange could not be completed (network, 5xx) - the session may well be
 *                    alive, so the person is NOT signed out over a hiccup. */
export type SessionRefreshResult = "renewed" | "rejected" | "unavailable";
type SessionRefresher = () => Promise<SessionRefreshResult>;
let sessionRefresher: SessionRefresher | undefined;

/** Registered by `auth-service.ts` - the one owner of the exchange (same cycle-avoidance as above). */
export function setSessionRefresher(refresher: SessionRefresher): void {
  sessionRefresher = refresher;
}

/**
 * Fetch wrapper for AUTHENTICATED calls - every api/* function that would otherwise write
 * `fetch(\`${API_BASE_URL}${path}\`, { headers: { ...authHeader() } })` by hand. Attaches the
 * bearer token. On a 401 it is the single place that decides what that means (SCRUM-729):
 *
 *  1. the access token (15 minutes) has simply expired - ask `auth-service` to exchange the refresh
 *     token, then repeat the request ONCE with the new access token;
 *  2. only if the exchange is refused is the session over: `unauthorizedHandler` (forceLogout).
 *
 * Several requests may 401 at once (a page firing riders + account in parallel). They all wait on
 * the same single exchange inside `auth-service` - refresh tokens rotate, so a second exchange with
 * the same token would look like theft and sign everybody out. A request that was sent with an
 * access token that has since been replaced does not exchange at all; it just repeats.
 *
 * Still returns the raw `Response` so `parseOrThrow`/callers keep throwing `ApiError` as before.
 */
export async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const sentWith = getStoredToken();
  const send = (): Promise<Response> =>
    fetch(`${API_BASE_URL}${path}`, { ...init, headers: { ...init.headers, ...authHeader() } });

  const res = await send();
  if (res.status !== 401) return res;

  if (sessionRefresher) {
    const tokenChanged = getStoredToken() !== sentWith && getStoredToken() !== null;
    const outcome: SessionRefreshResult = tokenChanged ? "renewed" : await sessionRefresher();
    if (outcome === "renewed") {
      const retried = await send();
      if (retried.status === 401) unauthorizedHandler?.(); // a FRESH token was refused: nothing more to try
      return retried;
    }
    if (outcome === "unavailable") return res;
  }
  unauthorizedHandler?.();
  return res;
}
